import { useToggle, useClickAway } from 'react-use';
import * as _ from 'lodash-es';
import { useTranslation } from 'react-i18next';
import languageSwitcherSprite from 'public/static/images/language-switcher-sprite.png';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import changeEvent from 'lib/Tracking/Footer/ChangeEvent';
import tapEvent from 'lib/Tracking/Footer/TapEvent';
import useEvents from 'lib/hooks/useEvents';
import { SourcePages } from 'lib/EventSchema';
import { colors, screen } from './styles/variables';

export default function LanguageSwitcher(): JSX.Element {
  const [isListShown, toggleIsListShown] = useToggle(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickAway(ref, () => {
    toggleIsListShown(false);
  });
  const { t } = useTranslation();
  const logger = useEvents();
  const router = useRouter();

  function switchLanguage(locale: string): void {
    logger.log(changeEvent({ locale, pathName: router.asPath as SourcePages }));

    // don't need to switch if already using the selected language
    if (locale === router.locale) {
      return;
    }

    // eslint-disable-next-line no-alert
    if (window.confirm(t('languageSwitcher.redirectionConfirmation'))) {
      // SRP is wrapping VDP in an iframe.
      // To go change locale url, message the SRP with a 'loc' message.
      window.parent.postMessage(['loc', locale], '*');
      router.push(router.pathname, router.asPath, { locale });
    }
  }

  const arrowDirection = isListShown ? 'down' : 'up';

  return (
    <div className="language-switcher" ref={ref}>
      <List isVisible={isListShown} onSelect={switchLanguage} />
      <button
        className="label"
        onClick={(): void => {
          toggleIsListShown();
          logger.log(tapEvent({ action: 'language', pathName: router.asPath as SourcePages }));
        }}
      >
        <div className="icon icon--en" />
        {t('languageSwitcher.label')}
        <div className={`arrow arrow--${arrowDirection}`} />
      </button>
      <style jsx>
        {`
          .language-switcher {
            display: inline-block;
            position: relative;
          }

          @media (min-width: ${screen.smMin}) {
            .language-switcher {
              position: relative;
              margin: 0;
              width: auto;
              text-align: left;
              bottom: auto;
            }
          }

          .label {
            position: relative;
            border: 1px solid ${colors.G2};
            padding: 1px 8px 4px 5px;
            font-size: 13px;
            display: inline-block;
            border-radius: 17px;
            line-height: 16px;
            margin-right: 15px;
            cursor: pointer;
            user-select: none;
            background: none;
            cursor: pointer;
            outline: inherit;
          }

          @media (min-width: ${screen.smMin}) {
            .label {
              margin-right: 0;
            }
          }

          .icon {
            display: inline-block;
            height: 15px;
            width: 22px;
            position: relative;
            top: 2px;
            margin-right: 3px;
            background-image: url(${languageSwitcherSprite});
          }

          .icon--en {
            background-size: 68px 33px;
            background-position: -44px 0;
          }

          .arrow {
            background-image: url(${languageSwitcherSprite});
            display: inline-block;
            height: 15px;
            width: 15px;
            position: relative;
            top: 3px;
            transform: rotate(90deg);
          }

          .arrow--up {
            background-size: 74px 43px;
            background-position: -24px -24px;
          }

          .arrow--down {
            background-size: 73px 43px;
            background-position: 0px -24px;
          }
        `}
      </style>
    </div>
  );
}

interface ListProps {
  isVisible: boolean;
  onSelect(locale: string): void;
}

function List({ isVisible, onSelect }: ListProps): JSX.Element {
  const { i18n } = useTranslation();
  const availableLocales = _.without(i18n.options.supportedLngs || [], 'cimode');
  const items = availableLocales.map((locale) => (
    <Item key={locale} isActive={locale === i18n.language} locale={locale} onClick={onSelect} />
  ));

  return (
    <ul
      className={isVisible ? 'language-switcher-list' : 'hidden language-switcher-list'}
      role="menu"
    >
      {items}
      <style jsx>
        {`
          .language-switcher-list {
            position: absolute;
            background-color: ${colors.G1};
            border-radius: 8px;
            padding: 15px;
            width: 189px;
            height: 96px;
            font-size: 16px;
            text-align: left;
            margin: 0 0 0 -91.5px;
            left: 192px;
            top: 0;
            bottom: 24px;
            list-style-type: none;
            user-select: none;
            z-index: 5;
          }
        `}
      </style>
    </ul>
  );
}

interface ItemProps {
  locale: string;
  isActive: boolean;
  onClick(locale: string): void;
}

function Item({ locale, isActive, onClick }: ItemProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <li
      className={isActive ? 'active language-switcher-list-item' : 'language-switcher-list-item'}
      onClick={(): void => onClick(locale)}
    >
      <div className={`icon icon--${locale}`} />
      {t(`languageSwitcher.language${_.startCase(locale)}`)} (
      {t(`languageSwitcher.languageInverse${_.startCase(locale)}`)})
      <style jsx>
        {`
          .language-switcher-list-item {
            padding-bottom: 10px;
            padding-left: 5px;
            padding-right: 5px;
            color: ${colors.G4};
            cursor: pointer;
          }

          li:last-child {
            padding-bottom: 0;
          }

          li.active {
            font-weight: 600;
            color: ${colors.BLACK};
            cursor: not-allowed;
          }

          .icon {
            margin-right: 5px;
            background-image: url(${languageSwitcherSprite});
            display: inline-block;
            height: 20px;
            width: 20px;
            position: relative;
            top: 6px;
          }

          .icon--en {
            background-size: 72px 37px;
            background-position: -25px -39px;
          }

          .icon--es {
            background-size: 72px 37px;
            background-position: -2px -39px;
          }
        `}
      </style>
    </li>
  );
}
